import { useEffect, useState, useMemo } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { useForm } from 'react-hook-form';
import { mutate } from "swr";
import { FormErrorMessage } from "../../components";
import { ApiSchemaCreateVendorLanguage } from '../../models';
import { PlatformType } from "../../configs";
import { useFunctionalityVendorLanguage, useFunctionalityPlatformLanguages, useFunctionalityLanguage } from '../../pages/hooks';

type Props = {
  show: boolean;
  handleShow: (value: boolean) => void;
  pagedVendorLanguageUrl: string;
  languageList: { value: string; label: string }[];
  isLoadingLangList: boolean;
  dataToEdit?: ApiSchemaCreateVendorLanguage,
  setDataToEdit?: (value?: ApiSchemaCreateVendorLanguage) => void
}

export function ModalAddVendorLanguage(props: Props): JSX.Element {
  const {
    show,
    handleShow,
    pagedVendorLanguageUrl,
    languageList,
    isLoadingLangList,
    dataToEdit,
    setDataToEdit
  } = props;

  const {
    vendorLanguageList,
    onAddLanguage,
    onUpdateLanguage
  } = useFunctionalityVendorLanguage(
    { setAddModalShow: handleShow, shouldVendorLanguageGetAllInvoke: true }
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<ApiSchemaCreateVendorLanguage>({
    defaultValues: {
      languageId: "",
      name: "",
      code: "",
    },
  });

  useEffect(() => {
    if (dataToEdit) {
      reset({
        languageId: dataToEdit.languageId,
        name: dataToEdit.name,
        code: dataToEdit.code,
      });
    } else {
      reset({
        languageId: "",
        name: "",
        code: "",
      });
    }
  }, [dataToEdit, reset]);

  const takenIds = useMemo(() => {
    const arr = vendorLanguageList ?? [];
    const currentId = dataToEdit?.languageId;

    const ids = arr
      .map((x: any) => x?.languageId ?? x?.id)
      .filter((id: string | undefined) => id && id !== currentId);

    return new Set(ids);
  }, [vendorLanguageList, dataToEdit?.languageId]);

  const availableLangOptions = useMemo(() => {
    const src = languageList ?? [];
    return src.filter(opt => !takenIds.has(opt.value));
  }, [languageList, takenIds]);

  // 🔹 NEW: build sets of taken names & codes (excluding the row being edited)
  const { takenNames, takenCodes } = useMemo(() => {
    const arr = vendorLanguageList ?? [];
    const currentName = dataToEdit?.name?.trim().toLowerCase();
    const currentCode = dataToEdit?.code?.trim().toLowerCase();

    const names = new Set<string>();
    const codes = new Set<string>();

    arr.forEach((x: any) => {
      const n = (x?.name ?? "").trim().toLowerCase();
      const c = (x?.code ?? "").trim().toLowerCase();

      if (n && n !== currentName) names.add(n);
      if (c && c !== currentCode) codes.add(c);
    });

    return { takenNames: names, takenCodes: codes };
  }, [vendorLanguageList, dataToEdit?.name, dataToEdit?.code]);

  const onCreateHandler = (form: ApiSchemaCreateVendorLanguage) => {
    const toSubmit: ApiSchemaCreateVendorLanguage = {
      ...form,
      name: form.name.trim(),
      code: form.code.trim(),
    };

    onAddLanguage(toSubmit)
      .then(() => {
        mutate(pagedVendorLanguageUrl);
        setDataToEdit?.(undefined);
        handleShow(false);
      })
      .catch((e: any) => { console.error(e) });
  };

  const onEdit = async (form: ApiSchemaCreateVendorLanguage) => {
    if (!dataToEdit) {
      console.log("Missing dataToEdit");
      return;
    }
    const id = (dataToEdit as any)?.id;

    const toSubmit: ApiSchemaCreateVendorLanguage = {
      vendorId: dataToEdit?.vendorId ?? form.vendorId ?? "",
      languageId: form.languageId,
      name: form.name.trim(),
      code: form.code.trim(), // 🔹 also trim on edit
    };

    try {
      await onUpdateLanguage(id, toSubmit).then(() => {
        mutate(pagedVendorLanguageUrl);
        setDataToEdit?.(undefined);
        handleShow(false);
      });
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <Modal
      show={show}
      onHide={() => { setDataToEdit?.(undefined); handleShow(false); }}
      centered
      id="addVendorLanguageModal"
    >
      <Modal.Header closeButton>
        <Modal.Title className="test-modal">
          {dataToEdit ? 'Edit Language' : 'Add Language'}
        </Modal.Title>
      </Modal.Header>

      <form onSubmit={handleSubmit(dataToEdit ? onEdit : onCreateHandler)}>
        <Modal.Body>

          <div className="row">
            <div className="col-12">
              <div className="form-group">
                <label htmlFor="" className="position-relative required">Language</label>
                {isLoadingLangList && (
                  <div className="d-flex align-items-center gap-2 mb-2" aria-live="polite">
                    <Spinner animation="border" size="sm" />
                    <span className="text-muted small ml-2">Loading languages…</span>
                  </div>
                )}
                <select
                  className="form-control"
                  disabled={isLoadingLangList || (availableLangOptions.length === 0 && !dataToEdit)}
                  {...register('languageId', {
                    required: "Language is required",
                    validate: (v) => v !== "" || "Language is required",
                  })}
                  defaultValue={dataToEdit?.languageId}
                >
                  <option value="" disabled>
                    {isLoadingLangList
                      ? "Loading…"
                      : availableLangOptions.length === 0 && !dataToEdit
                        ? "All languages already added"
                        : "Select Language"}
                  </option>
                  {availableLangOptions?.map((lang) => (
                    <option key={lang.value} value={lang.value}>
                      {lang.label}
                    </option>
                  ))}
                </select>
                {errors.languageId && (
                  <FormErrorMessage message={String(errors.languageId.message ?? "required")} />
                )}
              </div>
            </div>
          </div>

          {/* Vendor Language Name */}
          <div className="row">
            <div className="col-12">
              <div className="form-group">
                <label htmlFor="" className="position-relative required">Vendor Language Name</label>
                <input
                  type="text"
                  {...register('name', {
                    required: "required",
                    validate: (value) => {
                      const v = value?.trim();
                      if (!v) return "required";
                      if (takenNames.has(v.toLowerCase())) {
                        return "Vendor language name already exists";
                      }
                      return true;
                    },
                  })}
                  defaultValue={dataToEdit?.name}
                  className="form-control"
                  placeholder="Vendor Language Name"
                />
                {errors.name && (
                  <FormErrorMessage message={String(errors.name.message ?? "required")} />
                )}
              </div>
            </div>
          </div>

          {/* Vendor Code */}
          <div className="row">
            <div className="col-12">
              <div className="form-group">
                <label htmlFor="" className="position-relative required">Vendor Code</label>
                <input
                  type="text"
                  {...register('code', {
                    required: "required",
                    validate: (value) => {
                      const v = value?.trim();
                      if (!v) return "required";
                      if (takenCodes.has(v.toLowerCase())) {
                        return "Vendor code already exists";
                      }
                      return true;
                    },
                  })}
                  defaultValue={dataToEdit?.code}
                  className="form-control"
                  placeholder="Vendor Language Code"
                />
                {errors.code && (
                  <FormErrorMessage message={String(errors.code.message ?? "required")} />
                )}
              </div>
            </div>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <button
            type="button"
            className="btn btn-link text-site text-uppercase"
            onClick={() => { setDataToEdit?.(undefined); handleShow(false); }}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary text-uppercase login-from-submit-btn"
          >
            {dataToEdit ? 'Update' : 'Create'}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
